import { useCallback, useEffect, useState } from 'react'
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { findScenario } from '../../content/scenarios'
import { useAuth } from '../../lib/auth'
import Threshold from '../../components/Threshold'
import ResetPlaythrough from '../../components/ResetPlaythrough'
import StubPage from '../../components/StubPage'
import NotLetIn from '../../components/NotLetIn'
import DoorChooser from './DoorChooser'
import Inhabit from '../../surfaces/inhabit'
import Desk from '../../surfaces/desk'
import Messaging from '../../surfaces/messaging'

/**
 * One address for every pack: /p/:packId.
 *
 * The display grammar is a component choice, not a URL — the manifest already
 * declares which house a pack runs in, so the address bar carries nothing the
 * player would recognise.
 *
 * Grammar (which house) and device class (which body) are separate axes: this
 * picks the house, and the surface itself picks the body. See lib/device.ts.
 *
 * A scenario with DOORS gets a third step in front: the chooser. `?door=` names
 * the chosen door, and that door's compiled pack — not the scenario id — is what
 * the surface actually loads.
 */
export default function PackSurface() {
  const { packId } = useParams()
  const [search, setSearch] = useSearchParams()
  const { status, refresh } = useAuth()
  const scenario = findScenario(packId)

  /*
   * ⚠ ASK AGAIN BEFORE REFUSING ANYONE (2026-08-29: *"does the user need to refresh after being
   * let in?"* — they did, and that was the bug).
   *
   * `AuthProvider` fetches `/api/me` ONCE, at the root, on mount. Approval is a change made on the
   * server with no channel to a running client, so a reader whose tab was open when the founder let
   * them in went on being told `member` — and every doorless pack bounced them to the shelf with no
   * explanation. Two testers hit it on consecutive days and the only remedy anybody had was *please
   * refresh*, which is a workaround being taught to the people least able to understand it.
   *
   * Supabase's `autoRefreshToken` does eventually fire `TOKEN_REFRESHED`, which re-asks — so it
   * self-heals after roughly a token lifetime. An hour is not a fix.
   *
   * ⚠ ONLY ON THE FAILURE PATH, and only once. An approved reader never reaches this, so the normal
   * way in costs nothing extra; `asked` makes a refused one cost exactly one call rather than a
   * request per render. The gate that matters is still the server's — `/api/session` is
   * `AccessTier.Approved` whatever this decides.
   */
  const [asked, setAsked] = useState(false)
  const [checking, setChecking] = useState(false)
  useEffect(() => {
    if (status === 'approved' || asked) return
    setAsked(true)
    setChecking(true)
    void refresh().finally(() => setChecking(false))
  }, [status, asked, refresh])

  /*
   * STARTING OVER, once, above every grammar.
   *
   * Both hooks open a session on mount, so remounting IS the reset — `generation` is the key
   * that forces it. What differs between the houses is the resume handle: the desk keeps
   * `?session=` in the address bar, so a reset must also drop it or the fresh mount would
   * resume the very playthrough being abandoned. Messaging keeps none and simply reopens.
   *
   * Deliberately not held in either surface. A control that lived in the desk would have to be
   * written again for the thread, and the two would drift; this way the grammars stay ignorant
   * of it and the behaviour is the same wherever it is pressed.
   */
  /*
   * ⚠ HOISTED ABOVE EVERY EARLY RETURN, and it has to stay here (2026-08-29).
   *
   * It used to be declared beside the threshold it belongs to, several hundred lines down and
   * BELOW four conditional returns — `!scenario`, the door chooser, the locked branch and the
   * re-ask guard. React counts hooks positionally, so a render that returns early calls fewer of
   * them than the next one that does not, and the very first render to fall through past a branch
   * that had previously returned throws *"rendered more hooks than during the previous render"*.
   *
   * Latent until now, because the earlier returns were all stable for a given mount. The re-ask
   * guard is NOT: it returns null while `/api/me` is in flight and then falls through when the
   * answer lands, which is exactly the render pair that trips it. Declared here, the count is the
   * same on every render whatever any branch decides.
   */
  const [agreed, setAgreed] = useState<{ micros?: number } | null>(null)

  const [generation, setGeneration] = useState(0)
  const navigate = useNavigate()
  const resumeId = search.get('session')
  const reset = useCallback(() => {
    if (search.has('session')) {
      const next = new URLSearchParams(search)
      next.delete('session')
      setSearch(next, { replace: true })
    }
    setGeneration((g) => g + 1)
  }, [search, setSearch])

  if (!scenario) return <Navigate to="/" replace />

  // A member may read every pack page; only PLAYING needs approval. Handing an
  // unapproved reader the live surface would mean a session start that the
  // server refuses — a wall reached by clicking rather than a limit stated up
  // front. The chooser says it instead.
  //
  // This is presentation, not a control: AccessControl guards /api/session
  // regardless of what the bundle decides to render.
  const locked = status !== 'approved'

  /*
   * ⚠ NOTHING IS SAID UNTIL THE ANSWER IS BACK. Rendering the limit while the re-ask is still in
   * flight would flash "not yet" at somebody who WAS just let in — telling a reader they are shut
   * out and then contradicting it is worse than the half-second of nothing, and worse than the
   * bounce this replaced. `App` already holds the same line while the token is checked.
   */
  if (locked && checking) return null

  // Doors first. An unknown or unbuilt door falls back to the chooser rather
  // than guessing — there is no sensible default bottom to the mountain.
  //
  // A door that IS chosen means the player has already committed on the pack
  // page, so the surface opens itself rather than asking again behind a second
  // screen. `enteredByDoor` carries that fact to the surface.
  let effectivePackId = scenario.id
  let enteredByDoor = false
  if (scenario.doors?.length) {
    const chosen = scenario.doors.find(
      (d) => d.id === search.get('door') && d.packId,
    )
    if (!chosen || locked) return <DoorChooser scenario={scenario} locked={locked} />
    effectivePackId = chosen.packId!
    enteredByDoor = true
  } else if (locked) {
    /*
     * ⚠ SAY SO, RATHER THAN BOUNCE (2026-08-29). This used to be `<Navigate to="/" />` — the shelf,
     * silently, on the grounds that a doorless pack has "nowhere honest to put the reader". The
     * reasoning was sound and the conclusion was backwards: the honest place is a sentence, and
     * *every* CHATS conversation is doorless, so this was the common case rather than the edge one.
     *
     * It cost three bug reports that all looked like *"you click it and get routed to the front
     * page"* and none of which could be told apart from the outside — an unapproved reader, an
     * expired token and a stale client all produced the identical wordless redirect. A doored pack
     * had been stating its limit all along (`DoorChooser locked`); this is the same courtesy where
     * there is no chooser to carry it.
     */
    return <NotLetIn title={scenario.title} />
  }

  /*
   * ONE PERSON'S THREAD, NAMED IN THE URL (2026-08-25 — the CONVERSATIONS shelf).
   *
   * `/p/:packId?door=<personaId>` opens a pack at one person's thread with no corridor in
   * between. The param is a PERSONA id here and the messaging surface matches it against the
   * wire's doors.
   *
   * ⚠ NOTHING GENERATES ONE SINCE PACK-PER-CONVERSATION (same evening): a conversation is a pack
   * with one persona in it and the surface enters it without being told which. This stays because
   * it costs nothing, keeps yesterday's links alive, and is the only way to address one room of a
   * hall.
   *
   * ⚠ THE SAME PARAM NAME MEANS A COMPILED DOOR PACK ON A SCENARIO WITH `doors`, ten lines up —
   * so it is forwarded ONLY when this page did not consume it itself. `enteredByDoor` is exactly
   * that fact. A pack cannot be in both states: one compiles doors, the other has personas.
   *
   * Undefined for every other pack, which is what keeps every other pack's behaviour identical.
   */
  const atPersona = enteredByDoor ? undefined : search.get('door') ?? undefined

  // `key` is the reset: a new generation is a new component, which is a new session.
  const played = (surface: React.ReactNode) => (
    <>
      <ResetPlaythrough sessionId={resumeId} onReset={reset} />
      <div key={generation} style={{ display: 'contents' }}>
        {surface}
      </div>
    </>
  )

  /*
   * ── THE ASK, BEFORE ANY DOOR (2026-08-27 — the wallet) ────────────────────────────────────
   *
   * `docs/guides/the-wallet.md` §1: a charge is preceded by an ask. The billable atom is opening
   * a session, so the ask belongs at the last moment before one is opened — here, above every
   * grammar, rather than inside each surface. One card, one place, and a new grammar inherits it.
   *
   * `agreed` is the exact micros the player said yes to, carried into `startSession` and checked
   * server-side against today's plan. `undefined` means no price applied — a pack nobody priced,
   * or a build with no wallet — and the surface opens as it always did.
   *
   * ⚠ THE THRESHOLD RENDERS NOTHING WHEN THERE IS NOTHING TO ASK, and calls `onEnter` itself, so
   * every unpriced pack behaves exactly as it did before this existed.
   */
  if (agreed === null) {
    return (
      <Threshold
        packId={effectivePackId}
        title={scenario.title}
        onEnter={(micros) => setAgreed({ micros })}
        onBack={() => navigate('/')}
      />
    )
  }
  const price = agreed.micros

  switch (scenario.grammar) {
    case 'desk':
      return played(<Desk packId={effectivePackId} autoBegin={enteredByDoor} agreedPriceMicros={price} />)
    case 'inhabit':
      return played(<Inhabit packId={effectivePackId} agreedPriceMicros={price} />)
    // One being, one thread. No door chooser and no threshold screen — the
    // opening run inside the surface is the threshold.
    /*
     * ⚠ NO STANDING RESET ON THIS GRAMMAR (founder-ruled 2026-08-23). The surface asks at its own
     * door instead — continue, or start again — once, before a session is opened, and only when
     * there is something to resume. A button that sits on the page all visit is a wall-break you
     * can hit by accident on a surface whose value is an accumulated relationship.
     *
     * The DESK keeps `played()`, and the reason is specific to it: it carries `?session=` in the
     * address bar, so returning always resumes and there is no way back to a clean start except
     * editing the URL by hand.
     */
    /*
     * ⚠ REVERSED 2026-09-14 (founder): the doorstep is gone and the thread always continues. The
     * standing control returns, quiet, in the corner, the same one the desk has, because a reader
     * who cannot start over is stuck and that is the louder wall-break. It is armed twice and its
     * armed word says the cost: everything is forgotten, and it cannot be undone. Rare by design
     * and mostly for development; a reader who wants a person reset on purpose can, and is told
     * what it means first. The 08-23 reasoning stands as the record of why it was once otherwise.
     */
    case 'messaging':
      return played(
        <Messaging
          packId={effectivePackId}
          theme={scenario.theme === 'hildur'}
          // The shelf row's door, or nothing. See `atPersona` above.
          door={atPersona}
          // ⚠ THE RESET HAS TO SAY SO NOW (2026-08-22). The thread resumes on mount, so a bare
          // remount would bring the abandoned playthrough straight back — the control would
          // look like it worked and change nothing. `generation > 0` means the guest pressed
          // it during this page-load, which is exactly when a fresh thread is wanted.
          startFresh={generation > 0}
          agreedPriceMicros={price}
        />,
      )
    case 'correspondence':
      return (
        <StubPage eyebrow="CORRESPONDENCE" title={scenario.title}>
          <p style={{ color: 'var(--muted-bright)', fontSize: 15 }}>
            This grammar is not built yet — a letter exchange with one mind,
            turn by turn.
          </p>
        </StubPage>
      )
  }
}
